import { Loader2, HelpCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getTypeConfig } from "@/constants/ui-mapping";

interface DocumentTypeBadgeProps {
  docType: string | null | undefined;
  status: string | null | undefined;
  /** Compact mode: icon only or smaller text */
  compact?: boolean;
  /** Show emoji alongside text */
  showEmoji?: boolean;
  className?: string;
}

/**
 * Determines if a document is still being analyzed.
 * Returns true if:
 * - status is 'processing' (active queue job)
 * - doc_type is null/undefined/other AND status is not 'complete'/'failed'/'needs_review'
 */
export function isDocumentAnalyzing(
  docType: string | null | undefined,
  status: string | null | undefined
): boolean {
  // Actively processing
  if (status === 'processing') return true;
  
  // Final states - never show as analyzing
  const finalStates = ['complete', 'failed', 'needs_review'];
  if (finalStates.includes(status || '')) return false;
  
  // Has valid type - not analyzing
  if (docType && docType !== 'other') return false;
  
  // doc_type is null/undefined/other AND not in a final state
  return true;
}

/**
 * Smart document type badge that:
 * - Shows "Analyse..." with spinner when document is being processed
 * - Shows "À classifier" when analysis finished but type unknown
 * - Shows the actual type badge when classification is complete
 */
export function DocumentTypeBadge({ 
  docType, 
  status,
  compact = false,
  showEmoji = true,
  className = ""
}: DocumentTypeBadgeProps) {
  const isAnalyzing = isDocumentAnalyzing(docType, status);
  
  // Document is being analyzed - show spinner with animated dots
  if (isAnalyzing) {
    return (
      <Badge 
        variant="outline" 
        className={`gap-1 text-muted-foreground ${className}`}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        {!compact && (
          <span className="inline-flex items-center">
            Analyse
            <span className="animate-pulse">...</span>
          </span>
        )}
      </Badge>
    );
  }
  
  // Analysis complete but type is still 'other' or null - needs manual classification
  if (!docType || docType === 'other') {
    return (
      <Badge 
        variant="secondary" 
        className={`gap-1 text-muted-foreground ${className}`}
      >
        <HelpCircle className="w-3 h-3" />
        {!compact && <span>À classifier</span>}
      </Badge>
    );
  }
  
  // Normal case - show type badge
  const config = getTypeConfig(docType);
  return (
    <Badge variant="outline" className={className}>
      {showEmoji && <span className="mr-1">{config.emoji}</span>}
      {!compact && config.label}
      {compact && showEmoji && null}
    </Badge>
  );
}

/**
 * Returns display label for document type, handling analyzing state
 */
export function getDocumentTypeLabel(
  docType: string | null | undefined,
  status: string | null | undefined,
  withEmoji = true
): string {
  if (isDocumentAnalyzing(docType, status)) {
    return "🔄 Analyse...";
  }
  
  if (!docType || docType === 'other') {
    return withEmoji ? "❓ À classifier" : "À classifier";
  }
  
  const config = getTypeConfig(docType);
  return withEmoji ? `${config.emoji} ${config.label}` : config.label;
}

/**
 * Returns just the emoji for document type (for icon displays)
 */
export function getDocumentTypeEmoji(
  docType: string | null | undefined,
  status: string | null | undefined
): string {
  if (isDocumentAnalyzing(docType, status)) {
    return "🔄";
  }
  
  if (!docType || docType === 'other') {
    return "❓";
  }
  
  return getTypeConfig(docType).emoji;
}
